import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useBranches } from '@/Pages/Operations/shared';
import { formatDateTime } from '@/lib/i18n';
import { Card, CardContent } from '@/components/ui/card';
import { Field, Select } from '@/components/ui/field';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import { apiError, type AbandonedCart } from './storefrontLib';

/**
 * Baskets filled and not finished — counts, values and timings, and
 * DELIBERATELY no contact details (ADR-0016 §2.8): the backend's own select
 * never returns a name, phone or email here, so there is nothing on this
 * screen that could become a re-marketing list by accident. `cartTotal` has
 * no currency on the record (the DTO carries none either), so it is shown as
 * the server's own decimal string rather than guessing a symbol.
 */
export default function AbandonedCartsTab() {
  const { t, i18n } = useTranslation();
  const branches = useBranches();

  const [branchId, setBranchId] = useState('');
  const [rows, setRows] = useState<AbandonedCart[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ data: AbandonedCart[] }>('/storefront/admin/abandoned-carts', {
        params: { limit: 100, branchId: branchId || undefined },
      })
      .then(({ data }) => setRows(data.data ?? []))
      .catch((e) => setError(apiError(e, t('storefront.carts.loadFailed'))))
      .finally(() => setLoading(false));
  }, [branchId, t]);

  useEffect(load, [load]);

  return (
    <div className="space-y-3">
      {branches.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <Field label={t('storefront.carts.branch')}>
              <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                <option value="">{t('workforce.timesheets.anyBranch')}</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </Field>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <LoadingState label={t('common.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : rows.length === 0 ? (
        <EmptyState title={t('storefront.carts.empty')} hint={t('storefront.carts.emptyHint')} />
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('storefront.carts.abandonedAt')}</TH>
                <TH>{t('storefront.carts.mode')}</TH>
                <TH className="text-end">{t('storefront.carts.items')}</TH>
                <TH className="text-end">{t('storefront.carts.total')}</TH>
              </TR>
            </THead>
            <tbody>
              {rows.map((cart) => (
                <TR key={cart.id}>
                  <TD>{formatDateTime(cart.abandonedAt, i18n.language)}</TD>
                  <TD>{t(`storefront.fulfillmentMode.${cart.fulfillmentMode}`)}</TD>
                  <TD className="text-end tabular-nums">{cart.cartItemCount}</TD>
                  <TD className="text-end tabular-nums">{cart.cartTotal ?? '—'}</TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </div>
  );
}
